/*
  assets/gcity-auth.js — GCITY-AUTH-IDENTITY-ROLE-OWNER-13
  Updated: 2026-09-15

  Sole frontend owner for:
  - Shopify customer identity presentation state.
  - GCityPartners platform-account projection state.
  - Presentation-only role normalization and workspace routing.

  Identity authority:
  - Shopify Customer Accounts.

  Platform account authority:
  - GCityPartners Core / gcity-core-api.

  Trusted presentation-role input:
  - customer.metafields.custom.user_role
  - fallback customer.metafields.gcity.account_type
  - resolved server-side only by snippets/glg-launch-runtime-safety-loader.liquid
    and injected as GCityAuthBootstrap.role.

  Presentation roles:
  - role_client
  - role_partner
  - role_glg_internal

  Compatibility boundary:
  - role_citizen remains a LittleG-only compatibility role.
  - Browser role state is navigation/presentation only and never grants access.

  Security boundary:
  - This runtime never creates or links GCityPartners accounts.
  - This runtime never derives or exposes customer_ref / account_id / organisation membership IDs.
  - This runtime never grants roles, provider approval, entitlements or resource permissions.
  - Missing trusted role resolves to unknown.
  - Missing platform-account projection resolves to unresolved for authenticated identities.
  - Protected APIs must revalidate identity, platform account, user mode, provider approval,
    organisation membership, resource ownership and action permission server-side.
*/
(function (window, document) {
  'use strict';

  var VERSION = 'GCITY-AUTH-IDENTITY-ROLE-OWNER-13';
  var RUNTIME_KEY = '__GCITY_AUTH_RUNTIME_VERSION__';

  var IDENTITY_AUTHORITY = 'shopify-customer-accounts';
  var PLATFORM_ACCOUNT_OWNER = 'gcity-core-api';
  var PRESENTATION_ROLE_OWNER = 'assets/gcity-auth.js';
  var AUTHORIZATION_BOUNDARY = 'server-side-api-revalidation-required';

  var ACCOUNT_FALLBACK = '/account';
  var LOGOUT_FALLBACK = '/account/logout';
  var ROLE_SELECT_FALLBACK = '/pages/account-role-select';
  var SHOPIFY_CUSTOMER_ACCOUNT_HOST = 'account.glhksolution.com';
  var CLIENT_PORTAL = '/pages/client-portal';
  var PARTNER_PORTAL = '/pages/gcitypartners-provider-portal';
  var INTERNAL_PORTAL = '/pages/glg-internal-ops';
  var LITTLEG_PORTAL = '/pages/littleg-dashboard';
  var CLIENT_PORTAL_ROOT = '[data-client-portal]';

  var PRESENTATION_ROLES = Object.freeze({
    role_client: true,
    role_partner: true,
    role_glg_internal: true,
  });

  var COMPATIBILITY_ROLES = Object.freeze({
    role_citizen: true,
  });

  var ROLE_ALIASES = Object.freeze({
    client: 'role_client',
    customer: 'role_client',
    role_client: 'role_client',
    partner: 'role_partner',
    provider: 'role_partner',
    vendor: 'role_partner',
    role_partner: 'role_partner',
    internal: 'role_glg_internal',
    admin: 'role_glg_internal',
    staff: 'role_glg_internal',
    glg_internal: 'role_glg_internal',
    role_glg_internal: 'role_glg_internal',
    citizen: 'role_citizen',
    member: 'role_citizen',
    user: 'role_citizen',
    family: 'role_citizen',
    role_citizen: 'role_citizen',
  });

  var PLATFORM_ACCOUNT_STATES = Object.freeze({
    none: true,
    unresolved: true,
    active: true,
    pending_claim: true,
    suspended: true,
  });

  var PLATFORM_ACCOUNT_LINK_STATES = Object.freeze({
    none: true,
    unresolved: true,
    linked: true,
    pending_claim: true,
  });

  var LOCAL_RESULT_KEYS = Object.freeze({
    saved: Object.freeze([
      'littleg_city_mission_cards',
      'littleg_city_mission_cards_v1',
      'littleg_offline_city_missions',
      'littleg_result_history',
      'littleg.cityMission.lastResult',
      'littleg_city_plus_one_history_v1',
      'drg_city_mission_history_v1',
    ]),
    review: Object.freeze([
      'gcitypartners_review_brief_draft_v1',
      'gcitypartners_review_brief_history_v1',
    ]),
  });

  var bootstrap = {};
  var routes = {};
  var currentState = null;
  var observer = null;
  var frame = null;
  var pendingScopes = new Set();
  var mountedClientPortals = new WeakSet();

  if (window[RUNTIME_KEY] === VERSION && window.GCityAuth) {
    if (typeof window.GCityAuth.configure === 'function') {
      window.GCityAuth.configure(window.GCityAuthBootstrap || {});
    }
    if (typeof window.GCityAuth.refresh === 'function') {
      window.GCityAuth.refresh(document);
    }
    return;
  }
  window[RUNTIME_KEY] = VERSION;

  function clean(value) {
    return String(value == null ? '' : value).trim();
  }

  function safeInternalPath(value, fallback) {
    var safeFallback = clean(fallback) || '/';
    var raw = clean(value || safeFallback);

    try {
      var url = new URL(raw, window.location.origin);

      if (url.origin !== window.location.origin) return safeFallback;
      if (!url.pathname || url.pathname.charAt(0) !== '/') return safeFallback;

      return url.pathname + url.search + url.hash;
    } catch (_error) {
      return safeFallback;
    }
  }

  function safeLoginUrl(value, fallback) {
    var safeFallback = clean(fallback) || ACCOUNT_FALLBACK;
    var raw = clean(value || safeFallback);

    try {
      var url = new URL(raw, window.location.origin);

      if (url.origin === window.location.origin) {
        if (!url.pathname || url.pathname.charAt(0) !== '/') return safeFallback;
        return url.pathname + url.search + url.hash;
      }

      if (url.protocol === 'https:' && url.hostname === SHOPIFY_CUSTOMER_ACCOUNT_HOST) {
        return url.href;
      }

      return safeFallback;
    } catch (_error) {
      return safeFallback;
    }
  }

  function nodes(scope, selector) {
    var root = scope || document;
    var output = [];

    if (root.matches && root.matches(selector)) output.push(root);

    if (root.querySelectorAll) {
      Array.prototype.push.apply(output, root.querySelectorAll(selector));
    }

    return output;
  }

  function roleKey(value) {
    return clean(value)
      .toLowerCase()
      .replace(/-/g, '_')
      .replace(/\s+/g, '_')
      .replace(/[^a-z0-9_]/g, '');
  }

  function roleValue(input) {
    if (!input || typeof input !== 'object') return input;

    return (
      input.role ||
      input.user_role ||
      input.customer_role ||
      input.portal_role ||
      input.account_role
    );
  }

  function normalizeRole(input) {
    var role = ROLE_ALIASES[roleKey(roleValue(input))] || 'unknown';

    if (PRESENTATION_ROLES[role] || COMPATIBILITY_ROLES[role]) {
      return role;
    }

    return 'unknown';
  }

  function isGCityPartnersPresentationRole(input) {
    return PRESENTATION_ROLES[normalizeRole(input)] === true;
  }

  function experienceFor(input) {
    var role = normalizeRole(input);

    if (role === 'role_partner') return 'gcitypartners';
    if (role === 'role_glg_internal') return 'glg_internal';
    if (role === 'role_client') return 'client';
    if (role === 'role_citizen') return 'littleg';

    return 'unknown';
  }

  function normalizeProjectionState(value, allowed, fallback) {
    var normalized = clean(value)
      .toLowerCase()
      .replace(/[\s-]+/g, '_');

    return allowed[normalized] ? normalized : fallback;
  }

  function normalizePlatformAccountState(value, fallback) {
    return normalizeProjectionState(value, PLATFORM_ACCOUNT_STATES, fallback || 'unresolved');
  }

  function defaultLinkStateForAccount(accountState) {
    if (accountState === 'none') return 'none';
    if (accountState === 'pending_claim') return 'pending_claim';

    if (accountState === 'active' || accountState === 'suspended') {
      return 'linked';
    }

    return 'unresolved';
  }

  function normalizePlatformAccountLinkState(value, fallback) {
    return normalizeProjectionState(value, PLATFORM_ACCOUNT_LINK_STATES, fallback || 'unresolved');
  }

  function configure(nextBootstrap) {
    bootstrap =
      nextBootstrap && typeof nextBootstrap === 'object'
        ? nextBootstrap
        : window.GCityAuthBootstrap || {};

    routes = bootstrap.routes && typeof bootstrap.routes === 'object' ? bootstrap.routes : {};

    CLIENT_PORTAL = safeInternalPath(routes.clientPortal, CLIENT_PORTAL);

    PARTNER_PORTAL = safeInternalPath(routes.providerPortal, PARTNER_PORTAL);

    INTERNAL_PORTAL = safeInternalPath(routes.internalOps, INTERNAL_PORTAL);

    LITTLEG_PORTAL = safeInternalPath(routes.littlegPortal, LITTLEG_PORTAL);

    return Object.freeze({
      login: safeLoginUrl(routes.login, ACCOUNT_FALLBACK),
      account: safeInternalPath(routes.account, ACCOUNT_FALLBACK),
      logout: safeInternalPath(routes.logout, LOGOUT_FALLBACK),
      roleSelect: safeInternalPath(routes.roleSelect, ROLE_SELECT_FALLBACK),
      clientPortal: CLIENT_PORTAL,
      providerPortal: PARTNER_PORTAL,
      internalOps: INTERNAL_PORTAL,
      littlegPortal: LITTLEG_PORTAL,
    });
  }

  function routeFor(input) {
    var role = normalizeRole(input);

    if (role === 'role_client') return CLIENT_PORTAL;
    if (role === 'role_partner') return PARTNER_PORTAL;
    if (role === 'role_glg_internal') return INTERNAL_PORTAL;
    if (role === 'role_citizen') return LITTLEG_PORTAL;

    return safeInternalPath(routes.roleSelect, ROLE_SELECT_FALLBACK);
  }

  function accountPath() {
    return safeInternalPath(routes.account, ACCOUNT_FALLBACK);
  }

  function logoutPath() {
    return safeInternalPath(routes.logout, LOGOUT_FALLBACK);
  }

  function roleSelectPath() {
    return safeInternalPath(routes.roleSelect, ROLE_SELECT_FALLBACK);
  }

  function currentReturnTo() {
    return safeInternalPath(
      window.location.pathname + window.location.search + window.location.hash,
      '/'
    );
  }

  function buildLoginUrl(returnTo) {
    var loginPath = safeLoginUrl(routes.login, ACCOUNT_FALLBACK);

    var target = safeInternalPath(returnTo || currentReturnTo(), '/');

    var url = new URL(loginPath, window.location.origin);

    var currentIsAccount = /^\/account(?:\/|$)/.test(window.location.pathname);

    if (!currentIsAccount && !url.searchParams.has('return_to')) {
      url.searchParams.set('return_to', target);
    }

    return safeLoginUrl(url.href, ACCOUNT_FALLBACK);
  }

  function verificationHint(value) {
    if (value === true) return true;

    return ['true', 'verified', 'approved'].indexOf(clean(value).toLowerCase()) !== -1;
  }

  function guestState() {
    return Object.freeze({
      authenticated: false,
      name: '',
      role: 'unknown',
      sub_role: '',
      presentation_verified: false,
      route: roleSelectPath(),

      identity_authority: IDENTITY_AUTHORITY,

      platform_account_owner: PLATFORM_ACCOUNT_OWNER,

      platform_account_state: 'none',

      platform_account_link_state: 'none',

      presentation_role_owner: PRESENTATION_ROLE_OWNER,

      role_source: 'guest',

      authorization_boundary: AUTHORIZATION_BOUNDARY,
    });
  }

  function serverRenderedState() {
    var data =
      window.CUSTOMER_DATA && typeof window.CUSTOMER_DATA === 'object' ? window.CUSTOMER_DATA : {};

    var authenticated = bootstrap.authenticated === true || data.isLoggedIn === true;

    if (!authenticated) {
      return guestState();
    }

    var role = normalizeRole(bootstrap.role);

    var platformAccountState = normalizePlatformAccountState(
      bootstrap.platformAccountState,
      'unresolved'
    );

    var platformAccountLinkState = normalizePlatformAccountLinkState(
      bootstrap.platformAccountLinkState,
      defaultLinkStateForAccount(platformAccountState)
    );

    var presentationVerified = verificationHint(
      bootstrap.presentationVerified != null ? bootstrap.presentationVerified : bootstrap.verified
    );

    return Object.freeze({
      authenticated: true,

      name: clean(bootstrap.customerName || data.name || data.first_name || data.firstName),

      role: role,

      sub_role: clean(bootstrap.subRole),

      presentation_verified: presentationVerified,

      route: routeFor(role),

      identity_authority: IDENTITY_AUTHORITY,

      platform_account_owner: PLATFORM_ACCOUNT_OWNER,

      platform_account_state: platformAccountState,

      platform_account_link_state: platformAccountLinkState,

      presentation_role_owner: PRESENTATION_ROLE_OWNER,

      role_source:
        clean(bootstrap.roleSource) ||
        (role === 'unknown' ? 'unassigned' : 'shopify-server-rendered-metafield'),

      authorization_boundary: AUTHORIZATION_BOUNDARY,
    });
  }

  function projectIdentityBoundary(node, state) {
    if (!node || !node.setAttribute) {
      return;
    }

    state = state && typeof state === 'object' ? state : guestState();

    node.setAttribute('data-identity-authority', IDENTITY_AUTHORITY);

    node.setAttribute('data-identity-state', state.authenticated ? 'authenticated' : 'guest');

    node.setAttribute('data-platform-account-owner', PLATFORM_ACCOUNT_OWNER);

    node.setAttribute(
      'data-platform-account-state',
      state.platform_account_state || (state.authenticated ? 'unresolved' : 'none')
    );

    node.setAttribute(
      'data-platform-account-link-state',
      state.platform_account_link_state || (state.authenticated ? 'unresolved' : 'none')
    );

    node.setAttribute('data-authorization-boundary', AUTHORIZATION_BOUNDARY);
  }

  function setDocumentState(state) {
    state = state && typeof state === 'object' ? state : guestState();

    currentState = state;

    var html = document.documentElement;

    projectIdentityBoundary(html, state);

    html.setAttribute('data-presentation-role', state.role || 'unknown');

    html.setAttribute('data-presentation-experience', experienceFor(state.role));

    html.setAttribute('data-presentation-role-owner', PRESENTATION_ROLE_OWNER);

    html.setAttribute('data-presentation-role-source', state.role_source || 'unknown');

    html.setAttribute('data-login-entry-contract', 'shopify-native-v3');

    return state;
  }

  function parseRoles(value) {
    return clean(value)
      .split(/[\s,|]+/)
      .map(normalizeRole)
      .filter(function (role, index, all) {
        return role !== 'unknown' && all.indexOf(role) === index;
      });
  }

  function normalizeAllowedRoles(value) {
    if (Array.isArray(value)) {
      return value.map(normalizeRole).filter(function (role, index, all) {
        return role !== 'unknown' && all.indexOf(role) === index;
      });
    }

    return parseRoles(value);
  }

  function isAllowed(state, allowedRoles) {
    var allowed = normalizeAllowedRoles(allowedRoles);

    return Boolean(
      state &&
      state.authenticated === true &&
      state.role !== 'unknown' &&
      (!allowed.length || allowed.indexOf(state.role) !== -1)
    );
  }

  function login(options) {
    options = options || {};

    var destination = buildLoginUrl(options.returnTo || currentReturnTo());

    if (options.move === false) {
      return destination;
    }

    window.location.assign(destination);

    return destination;
  }

  function redirectToRole(input) {
    var destination = routeFor(input);

    var current = window.location.pathname + window.location.search + window.location.hash;

    if (!destination || destination === current) {
      return false;
    }

    window.location.replace(destination);

    return true;
  }

  function session() {
    return Promise.resolve(setDocumentState(serverRenderedState()));
  }

  function optional() {
    return session().then(function (state) {
      return state.authenticated ? state : null;
    });
  }

  function requireLogin(options) {
    options = options || {};

    return session().then(function (state) {
      if (state.authenticated) {
        return state;
      }

      if (options.move !== false) {
        login({
          returnTo: options.returnTo,
          move: true,
        });
      }

      return null;
    });
  }

  function requireRole(allowedRoles, options) {
    options = options || {};

    var allowed = normalizeAllowedRoles(allowedRoles);

    return session().then(function (state) {
      if (!state.authenticated) {
        if (options.move !== false) {
          login({
            returnTo: options.returnTo,
            move: true,
          });
        }

        return null;
      }

      if (!allowed.length || state.role === 'unknown' || allowed.indexOf(state.role) === -1) {
        if (options.move !== false) {
          redirectToRole(state.role);
        }

        return null;
      }

      return state;
    });
  }

  function ensureLabel(anchor, text) {
    if (!anchor) return;

    var label = anchor.querySelector('[data-auth-label]');

    if (!label) {
      label = document.createElement('span');

      label.setAttribute('data-auth-label', '');

      anchor.appendChild(label);
    }

    if (label.textContent !== text) {
      label.textContent = text;
    }
  }

  function markEntry(anchor, source) {
    if (!anchor) return;

    anchor.classList.add('gcity-auth-entry');

    anchor.setAttribute('data-auth-entry', '');

    anchor.setAttribute('data-auth-source', source);

    var rel = new Set(clean(anchor.getAttribute('rel')).split(/\s+/).filter(Boolean));

    rel.add('nofollow');

    anchor.setAttribute('rel', Array.from(rel).join(' '));
  }

  function setVisible(node, visible) {
    if (!node) return;

    node.hidden = !visible;

    node.setAttribute('aria-hidden', visible ? 'false' : 'true');

    if (visible) {
      node.removeAttribute('tabindex');
    } else {
      node.setAttribute('tabindex', '-1');
    }
  }

  function isZh() {
    return (
      clean(bootstrap.locale || document.documentElement.lang || 'en')
        .toLowerCase()
        .indexOf('zh') === 0
    );
  }

  function labels() {
    return isZh()
      ? {
          signIn: '登入',
          account: '我的帳戶',
          provider: '合作夥伴工作區',
          internal: '內部工作區',
        }
      : {
          signIn: 'Sign in',
          account: 'My account',
          provider: 'Provider workspace',
          internal: 'Internal workspace',
        };
  }

  function enhanceSimpleEntry(anchor, source, state, label) {
    if (!anchor) return;

    markEntry(anchor, source);

    projectIdentityBoundary(anchor, state);

    anchor.href = state.authenticated ? accountPath() : buildLoginUrl(currentReturnTo());

    ensureLabel(anchor, label);
  }

  function enhanceRoleSelect(scope, state) {
    nodes(scope, '[data-account-role-select]').forEach(function (root) {
      projectIdentityBoundary(root, state);

      root.setAttribute('data-presentation-role-owner', PRESENTATION_ROLE_OWNER);

      root.setAttribute('data-portal-authenticated', state.authenticated ? 'true' : 'false');

      root.setAttribute('data-portal-role', state.role || 'unknown');

      root.setAttribute('data-portal-experience', experienceFor(state.role));

      var home = root.querySelector('[data-portal-home-link]');

      if (!home) return;

      if (!state.authenticated || state.role === 'unknown') {
        setVisible(home, false);

        return;
      }

      setVisible(home, true);

      home.href = routeFor(state.role);

      var copy = labels();

      if (state.role === 'role_partner') {
        home.textContent = copy.provider;
      } else if (state.role === 'role_glg_internal') {
        home.textContent = copy.internal;
      } else if (state.role === 'role_citizen') {
        home.textContent = 'LittleG';
      } else {
        home.textContent = copy.account;
      }
    });
  }

  function enhanceEntries(scope, state) {
    var root = scope || document;

    state = state || currentState || serverRenderedState();

    var copy = labels();

    nodes(root, '[data-app-sidebar-link="account"], [data-context-account-shortcut]').forEach(
      function (anchor) {
        if (anchor.getAttribute('data-identity-authority') === 'littleg-web-session') return;

        var source = anchor.hasAttribute('data-app-sidebar-link')
          ? 'sidebar-account'
          : 'context-account';

        var destination =
          state.authenticated && state.role !== 'unknown'
            ? routeFor(state.role)
            : state.authenticated
              ? accountPath()
              : buildLoginUrl(currentReturnTo());

        markEntry(anchor, source);

        projectIdentityBoundary(anchor, state);

        anchor.href = destination;
      }
    );

    nodes(root, '[data-lg-auth-login]').forEach(function (anchor) {
      var guestLabel = copy.signIn;
      // Account gateway has Shopify-configured copy even when the global locale is English.
      // Only its display label is overridden; session, URL and role resolution are unchanged.
      if (anchor.getAttribute('data-auth-source') === 'account-gateway') {
        guestLabel = anchor.getAttribute('data-auth-guest-label') || copy.signIn;
      }
      enhanceSimpleEntry(anchor, 'gate', state, state.authenticated ? copy.account : guestLabel);
    });

    nodes(root, '[data-portal-account-links]').forEach(function (nav) {
      projectIdentityBoundary(nav, state);

      nav.setAttribute('data-portal-authenticated', state.authenticated ? 'true' : 'false');

      nav.setAttribute('data-portal-role', state.role || 'unknown');

      nav.setAttribute('data-portal-experience', experienceFor(state.role));

      nav.setAttribute('data-portal-verified', state.presentation_verified ? 'true' : 'false');

      nav.setAttribute('data-auth-consumer', PRESENTATION_ROLE_OWNER);

      var home = nav.querySelector('[data-portal-home-link]');

      if (home && state.authenticated) {
        home.href = routeFor(state.role);
      }

      var experience = nav.querySelector('[data-portal-experience-label]');

      if (experience) {
        var projected = experienceFor(state.role);

        experience.textContent =
          projected === 'gcitypartners'
            ? 'GCityPartners'
            : projected === 'glg_internal'
              ? 'GLG Internal'
              : projected === 'littleg'
                ? 'LittleG'
                : projected === 'client'
                  ? copy.account
                  : 'Account';
      }
    });

    nodes(root, '[data-portal-login-link]').forEach(function (anchor) {
      setVisible(anchor, !state.authenticated);

      if (!state.authenticated) {
        enhanceSimpleEntry(anchor, 'portal', state, copy.signIn);
      }
    });

    nodes(root, '[data-portal-account-link]').forEach(function (anchor) {
      setVisible(anchor, state.authenticated);

      if (state.authenticated) {
        markEntry(anchor, 'portal-account');

        projectIdentityBoundary(anchor, state);

        anchor.href = accountPath();
      }
    });

    nodes(root, '[data-portal-logout-link]').forEach(function (anchor) {
      setVisible(anchor, state.authenticated);

      if (state.authenticated) {
        markEntry(anchor, 'portal-logout');

        projectIdentityBoundary(anchor, state);

        anchor.href = logoutPath();
      }
    });

    enhanceRoleSelect(root, state);
  }

  function requiredRoles(node) {
    if (!node || !node.getAttribute) {
      return [];
    }

    return parseRoles(
      node.getAttribute('data-required-role') || node.getAttribute('data-required-roles') || ''
    );
  }

  function applyRoleGuards(scope, state) {
    nodes(scope, '[data-required-role], [data-required-roles]').forEach(function (node) {
      var allowedRoles = requiredRoles(node);

      if (!allowedRoles.length) {
        return;
      }

      var allowed = isAllowed(state, allowedRoles);

      projectIdentityBoundary(node, state);

      node.setAttribute('data-presentation-role-owner', PRESENTATION_ROLE_OWNER);

      node.setAttribute('data-role-guard-authority', 'presentation-only');

      node.setAttribute('data-portal-authenticated', state.authenticated ? 'true' : 'false');

      node.setAttribute('data-portal-role', state.role || 'unknown');

      node.setAttribute('data-portal-role-allowed', allowed ? 'true' : 'false');

      if (
        node.getAttribute('data-role-router-mode') === 'redirect' &&
        state.authenticated &&
        !allowed
      ) {
        redirectToRole(state.role);
      }
    });
  }

  function safeParse(raw) {
    if (!raw) return null;

    try {
      return JSON.parse(raw);
    } catch (_error) {
      return null;
    }
  }

  function readStorage(storage, key) {
    try {
      return storage ? storage.getItem(key) || '' : '';
    } catch (_error) {
      return '';
    }
  }

  function asItems(value) {
    if (!value) return [];
    if (Array.isArray(value)) return value;

    if (typeof value === 'object') {
      var items = value.items || value.cards || value.results || value.history;

      return Array.isArray(items) ? items : [value];
    }

    return [value];
  }

  function identity(value) {
    if (value == null) {
      return '';
    }

    if (typeof value !== 'object') {
      return clean(value);
    }

    var id = clean(
      value.result_id ||
        value.record_id ||
        value.card_id ||
        value.cardId ||
        value.id ||
        value.mission_id ||
        value.key
    );

    if (id) {
      return id;
    }

    try {
      return JSON.stringify(value);
    } catch (_error) {
      return '';
    }
  }

  function countLocal(keys) {
    var seen = new Set();

    keys.forEach(function (key) {
      [readStorage(window.localStorage, key), readStorage(window.sessionStorage, key)].forEach(
        function (raw) {
          asItems(safeParse(raw)).forEach(function (item) {
            var id = identity(item);

            if (id) {
              seen.add(id);
            }
          });
        }
      );
    });

    return seen.size;
  }

  function hydrateClientPortal(root, state) {
    if (!(root instanceof HTMLElement)) return;

    projectIdentityBoundary(root, state);

    root.setAttribute('data-client-portal-access-authority', 'presentation-only');

    var saved = countLocal(LOCAL_RESULT_KEYS.saved);

    var review = countLocal(LOCAL_RESULT_KEYS.review);

    var resultStorage = window.LittleGResultStorage;

    if (resultStorage && typeof resultStorage.listAll === 'function') {
      try {
        var results = resultStorage.listAll();

        if (Array.isArray(results)) {
          saved = results.length;
        }
      } catch (_error) {}
    }

    var counts = {
      saved: saved,
      review: review,
    };

    Object.keys(counts).forEach(function (key) {
      var text = String(counts[key]);

      var target = root.querySelector('[data-client-count="' + key + '"]');

      if (target) {
        target.textContent = text;
      }

      root.setAttribute('data-client-return-count-' + key, text);
    });

    var total = saved + review;

    root.setAttribute('data-client-return-loop-total', String(total));

    root.setAttribute(
      'data-client-return-loop-state',
      total ? 'has-local-context' : 'empty-local-context'
    );

    root.setAttribute('data-client-portal-role', state.role || 'unknown');

    root.setAttribute(
      'data-client-portal-access',
      isAllowed(state, ['role_client']) ? 'ready' : 'restricted'
    );

    var roleNode = root.querySelector('[data-client-portal-role]');

    var statusNode = root.querySelector('[data-client-portal-status]');

    var returnNode = root.querySelector('[data-client-return-status]');

    if (roleNode) {
      roleNode.textContent = state.role || 'unknown';
    }

    if (statusNode) {
      statusNode.textContent = isAllowed(state, ['role_client'])
        ? 'Client portal ready.'
        : 'Client access is being verified.';
    }

    if (returnNode) {
      returnNode.textContent = total ? 'Saved context available' : 'Return loop ready';
    }
  }

  function refreshClientPortals(scope, state) {
    state = state || currentState || serverRenderedState();

    nodes(scope, CLIENT_PORTAL_ROOT).forEach(function (root) {
      if (!mountedClientPortals.has(root)) {
        mountedClientPortals.add(root);

        root.setAttribute('data-client-portal-runtime', VERSION);
      }

      hydrateClientPortal(root, state);
    });
  }

  function dispatchReady(state) {
    var detail = Object.freeze({
      version: VERSION,

      state: state,

      authenticated: state.authenticated,

      role: state.role,

      route: state.route,

      identityAuthority: IDENTITY_AUTHORITY,

      platformAccountOwner: PLATFORM_ACCOUNT_OWNER,

      platformAccountState: state.platform_account_state,

      platformAccountLinkState: state.platform_account_link_state,

      presentationRoleOwner: PRESENTATION_ROLE_OWNER,

      authorizationBoundary: AUTHORIZATION_BOUNDARY,
    });

    try {
      document.dispatchEvent(
        new CustomEvent('gcity:auth:ready', {
          detail: detail,
        })
      );
    } catch (_error) {}

    try {
      window.dispatchEvent(
        new CustomEvent('gcity:auth-entries:ready', {
          detail: detail,
        })
      );
    } catch (_error) {}
  }

  function refresh(scope) {
    var state = setDocumentState(serverRenderedState());

    enhanceEntries(scope || document, state);

    applyRoleGuards(scope || document, state);

    refreshClientPortals(scope || document, state);

    dispatchReady(state);

    return state;
  }

  function scheduleScopes(scopes) {
    scopes.forEach(function (scope) {
      if (scope && (scope.querySelectorAll || scope.matches)) {
        pendingScopes.add(scope);
      }
    });

    if (frame != null) {
      return;
    }

    frame = window.requestAnimationFrame(function () {
      frame = null;

      var state = setDocumentState(serverRenderedState());

      pendingScopes.forEach(function (scope) {
        enhanceEntries(scope, state);

        applyRoleGuards(scope, state);

        refreshClientPortals(scope, state);
      });

      pendingScopes.clear();

      dispatchReady(state);
    });
  }

  function startObserver() {
    if (observer || !document.body || typeof MutationObserver !== 'function') {
      return;
    }

    observer = new MutationObserver(function (records) {
      var scopes = [];

      records.forEach(function (record) {
        if (record.target && record.target.nodeType === Node.ELEMENT_NODE) {
          scopes.push(record.target);
        }

        Array.prototype.forEach.call(record.addedNodes || [], function (node) {
          if (node && node.nodeType === Node.ELEMENT_NODE) {
            scopes.push(node);
          }
        });
      });

      if (scopes.length) {
        scheduleScopes(scopes);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  configure(window.GCityAuthBootstrap || {});

  window.GCityAuth = Object.freeze({
    VERSION: VERSION,

    identityAuthority: IDENTITY_AUTHORITY,

    platformAccountOwner: PLATFORM_ACCOUNT_OWNER,

    presentationRoleOwner: PRESENTATION_ROLE_OWNER,

    roleSourceContract: 'custom.user_role -> gcity.account_type -> unknown',

    presentationRoles: Object.freeze(['role_client', 'role_partner', 'role_glg_internal']),

    littlegCompatibilityRole: 'role_citizen',

    authorizationBoundary: AUTHORIZATION_BOUNDARY,

    normalizeRole: normalizeRole,

    isGCityPartnersPresentationRole: isGCityPartnersPresentationRole,

    experienceFor: experienceFor,

    routeFor: routeFor,

    configure: configure,

    current: function () {
      return currentState || serverRenderedState();
    },

    session: session,

    optional: optional,

    requireLogin: requireLogin,

    requireRole: requireRole,

    isAllowed: isAllowed,

    buildLoginUrl: buildLoginUrl,

    login: login,

    refresh: refresh,

    enhanceEntries: enhanceEntries,

    refreshPortals: refreshClientPortals,

    endpoints: Object.freeze({
      login: safeLoginUrl(routes.login, ACCOUNT_FALLBACK),

      account: accountPath(),

      logout: logoutPath(),

      roleSelect: roleSelectPath(),
    }),
  });

  window.ClientPortalRuntime = Object.freeze({
    version: VERSION,

    refresh: function () {
      refreshClientPortals(document, window.GCityAuth.current());
    },

    mountAll: function (scope) {
      refreshClientPortals(scope || document, window.GCityAuth.current());
    },
  });

  function start() {
    refresh(document);
    startObserver();
  }

  window.addEventListener('storage', function () {
    refreshClientPortals(document, window.GCityAuth.current());
  });

  window.addEventListener('pageshow', function () {
    refresh(document);
  });

  document.addEventListener('shopify:section:load', function (event) {
    refresh(event.target || document);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})(window, document);
